import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { useOrgSoftwareConfig } from '@/hooks/queries/use-org-software-config';
import { useUpdateOrgSoftwareConfig } from '@/hooks/mutations/use-update-org-software-config';
import {
  type OrgSoftwareConfig,
  type UpdateOrgSoftwareConfig,
} from '@/services/org/org-software-config';
import {
  validateWorkflowBranch,
  validateWorkflowRepositoryFullName,
} from '@/utils/workflow-repo-validation';

const sourceLabel = (source: 'platform_fallback' | 'organization_override') =>
  source === 'organization_override'
    ? 'Organization override'
    : 'Platform default';

export const WorkflowSoftwareRepository = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const notification = useNotification();
  const configQuery = useOrgSoftwareConfig(orgId);
  const updateConfig = useUpdateOrgSoftwareConfig();
  const [repository, setRepository] = useState('');
  const [branch, setBranch] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const previousOrgId = useRef<string | undefined>(orgId);

  useEffect(() => {
    if (previousOrgId.current !== orgId) {
      previousOrgId.current = orgId;
      setRepository('');
      setBranch('');
      setIsDirty(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (configQuery.data && !isDirty) {
      setRepository(configQuery.data.default_workflow_repo);
      setBranch(configQuery.data.default_workflow_branch);
    }
  }, [configQuery.data, isDirty]);

  const trimmedRepository = repository.trim();
  const trimmedBranch = branch.trim();
  const repositoryError = validateWorkflowRepositoryFullName(trimmedRepository);
  const branchError = validateWorkflowBranch(trimmedBranch);
  const hasChanges =
    !!configQuery.data &&
    (trimmedRepository !== configQuery.data.default_workflow_repo ||
      trimmedBranch !== configQuery.data.default_workflow_branch);

  const applyResult = (config: OrgSoftwareConfig) => {
    setRepository(config.default_workflow_repo);
    setBranch(config.default_workflow_branch);
    setIsDirty(false);
  };

  const runUpdate = async (
    update: UpdateOrgSoftwareConfig,
    successMessage: string,
  ) => {
    if (!orgId) return;

    try {
      const config = await updateConfig.mutateAsync({ orgId, update });
      applyResult(config);
      notification.success(successMessage);
    } catch (error) {
      notification.error(
        error instanceof Error
          ? error.message
          : 'Failed to update workflow repository defaults',
      );
    }
  };

  const handleSave = () => {
    if (!configQuery.data || repositoryError || branchError || !hasChanges) {
      return;
    }

    const update: UpdateOrgSoftwareConfig = {};
    if (trimmedRepository !== configQuery.data.default_workflow_repo) {
      update.default_workflow_repo = trimmedRepository;
    }
    if (trimmedBranch !== configQuery.data.default_workflow_branch) {
      update.default_workflow_branch = trimmedBranch;
    }

    void runUpdate(update, 'Workflow repository defaults saved');
  };

  const handleReset = () => {
    void runUpdate(
      {
        default_workflow_repo: '',
        default_workflow_branch: '',
      },
      'Workflow repository defaults reset to the platform defaults',
    );
  };

  return (
    <Card>
      <CardContent className='flex flex-col gap-5'>
        <div>
          <div className='text-zinc-900 dark:text-zinc-50'>
            Workflow software repository
          </div>
          <p className='mt-1 text-sm text-muted-foreground'>
            Prefill the repository and branch for future workflows. Existing
            workflows keep their saved settings, and creators can override or
            disable the repository per workflow.
          </p>
        </div>

        {configQuery.isLoading && (
          <div
            className='flex items-center gap-2 text-sm text-muted-foreground'
            role='status'
          >
            <LoadingSpinner />
            Loading workflow repository defaults...
          </div>
        )}

        {configQuery.isError && (
          <div
            className='flex items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200'
            role='alert'
          >
            <div className='flex items-center gap-2'>
              <AlertTriangle className='size-4 shrink-0' />
              <span>
                {configQuery.error?.message ||
                  'Could not load workflow repository defaults.'}
              </span>
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() => void configQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        )}

        {configQuery.data && (
          <>
            <div className='flex flex-col gap-1.5'>
              <div className='flex items-center justify-between gap-3'>
                <Label htmlFor='default-workflow-repository'>
                  GitHub repository
                </Label>
                <span className='text-xs text-muted-foreground'>
                  {sourceLabel(configQuery.data.default_workflow_repo_source)}
                </span>
              </div>
              <Input
                id='default-workflow-repository'
                value={repository}
                onChange={(event) => {
                  setRepository(event.target.value);
                  setIsDirty(true);
                }}
                placeholder='owner/repository'
                autoComplete='off'
                aria-invalid={!!repositoryError}
              />
              {repositoryError ? (
                <p className='text-xs text-rose-600 dark:text-rose-400'>
                  {repositoryError}
                </p>
              ) : (
                <p className='text-xs text-muted-foreground'>
                  Canonical owner/name only; URLs are not accepted. Clearing
                  this field resets it to the platform default.
                </p>
              )}
            </div>

            <div className='flex flex-col gap-1.5'>
              <div className='flex items-center justify-between gap-3'>
                <Label htmlFor='default-workflow-branch'>Branch or ref</Label>
                <span className='text-xs text-muted-foreground'>
                  {sourceLabel(configQuery.data.default_workflow_branch_source)}
                </span>
              </div>
              <Input
                id='default-workflow-branch'
                value={branch}
                onChange={(event) => {
                  setBranch(event.target.value);
                  setIsDirty(true);
                }}
                placeholder='branch-or-ref'
                autoComplete='off'
                aria-invalid={!!branchError}
              />
              {branchError ? (
                <p className='text-xs text-rose-600 dark:text-rose-400'>
                  {branchError}
                </p>
              ) : (
                <p className='text-xs text-muted-foreground'>
                  Clearing this field resets it to the platform default.
                </p>
              )}
            </div>

            <div className='flex flex-wrap justify-end gap-2'>
              <Button
                variant='outline'
                onClick={handleReset}
                disabled={updateConfig.isPending}
              >
                Reset to platform default
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  updateConfig.isPending ||
                  !hasChanges ||
                  !!repositoryError ||
                  !!branchError
                }
              >
                {updateConfig.isPending ? (
                  <>
                    <LoadingSpinner /> Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
